import * as React from "react"
import { useToast } from "@/hooks/use-toast"
import { AlertCircle, CheckCircle2, Info } from "lucide-react"
import {
  Toast,
  ToastAction,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "@/components/ui/toast"
import type { ToastActionConfig } from "@/hooks/use-toast"

/**
 * Renders an appropriate icon based on toast variant
 */
function ToastIcon({ variant }: { variant?: string }) {
  if (variant === 'destructive') {
    return (
      <div className="flex-shrink-0 p-2 rounded-xl bg-gradient-to-br from-red-500/15 to-red-600/10 ring-1 ring-red-500/10">
        <AlertCircle className="h-4 w-4 text-red-400" />
      </div>
    );
  }
  return (
    <div className="flex-shrink-0 p-2 rounded-xl bg-gradient-to-br from-purple-500/15 to-purple-600/10 ring-1 ring-purple-500/10">
      <Info className="h-4 w-4 text-purple-400" />
    </div>
  );
}

function isToastActionConfig(action: unknown): action is ToastActionConfig {
  return (
    typeof action === "object" &&
    action !== null &&
    "label" in action &&
    typeof (action as ToastActionConfig).label === "string" &&
    "onClick" in action &&
    typeof (action as ToastActionConfig).onClick === "function"
  )
}

export function Toaster() {
  const { toasts } = useToast()

  return (
    <ToastProvider>
      {toasts.map(function ({ id, title, description, action, variant, ...props }) {
        return (
          <Toast key={id} variant={variant} {...props}>
            <div className="flex items-start gap-3.5 w-full">
              <ToastIcon variant={variant as string} />
              <div className="grid gap-1.5 flex-1 min-w-0">
                {title && <ToastTitle>{title}</ToastTitle>}
                {description && (
                  <ToastDescription>{description}</ToastDescription>
                )}
              </div>
            </div>
            {React.isValidElement(action) && action}
            {isToastActionConfig(action) && (
              <ToastAction
                altText={action.altText ?? action.label}
                onClick={action.onClick}
              >
                {action.label}
              </ToastAction>
            )}
            <ToastClose />
          </Toast>
        )
      })}
      <ToastViewport />
    </ToastProvider>
  )
}
